// Several tickets from one call, before any of them exists.
//
// The (+) under the main ticket on the create screen adds a CHILD: another issue the
// same caller raised on the same call, filed as its own ticket, worked and closed on
// its own, and linked to the first as its parent (docs/API.md §3.3 `parent_ticket_id`).
// An agent can add as many as the call produced and file them all with one click, rather
// than creating the first ticket, opening it, pressing (+) and coming back for each.
//
// A child starts as a copy of the main ticket's classification — form, priority, queue,
// owner, tags, device, custom fields — because a second issue on the same call is usually
// filed the same way, and "change the one thing that differs" is one edit where "fill it
// in again" is ten. The caller, organization, location, CCs and followers are NOT copied
// onto the child draft: they are the SAME call, so they are read from the main ticket at
// submit time and cannot drift from it.
//
// ABSENT VERSUS NULL, per child. A child carries its own `touched`: the form, queue and
// owner its agent CHOSE (drafts/draft-model.js `childTouched`). It starts as the main
// ticket's, and changing one of those three on the card adds it. The rest are only shown
// (the owner as the agent looking at the screen, the form and queue as today's defaults),
// are left out of what is saved, and are left out of what is filed (drafts/draft-flow.js
// `fileRequest`), so they resolve at filing: an untouched owner is whoever files.
//
// Everything here is PURE — drafts, validation, request bodies — so the shape a child
// posts as can be pinned in a test without a browser. The screen only renders and
// sequences: parent first, then each child with the parent's id.

import { encodeCustomFields, invalidNumberFields, missingRequiredFields } from './custom-field-values.js';
import { DEFAULTABLE, childTouched } from '../drafts/draft-model.js';

let counter = 0;

/** A key that survives re-renders and reordering; not an id, never sent. */
export function nextChildKey(now = Date.now()) {
  counter += 1;
  return `c${now.toString(36)}${counter}`;
}

/**
 * A new child, copied from the main ticket as it stands right now.
 *
 * @param {object} parentFields    New.jsx's `fields` slice for the main ticket.
 * @param {object} parentComposer  New.jsx's `composer` slice — the subject is copied,
 *   to be edited into the sibling's ("… — receipt printer"), the body is not.
 * @param {string} [key]
 * @param {Iterable<string>} [parentTouched]  The main ticket's `touched`: what the parent's
 *   agent chose is chosen for the child too; what is only displayed on the parent stays
 *   only displayed on the child. A copy, never the parent's own array.
 */
export function newChildDraft(parentFields, parentComposer, key = nextChildKey(), parentTouched = []) {
  const chosen = new Set(parentTouched ?? []);
  return {
    key,
    touched: DEFAULTABLE.filter((field) => chosen.has(field)),
    composer: {
      subject: parentComposer?.subject ?? '',
      body_text: '',
      is_internal_note: true,
    },
    fields: {
      form_id: parentFields.form_id ?? '',
      priority: parentFields.priority ?? 'normal',
      // A child starts `new` whatever the main ticket is being submitted as: the
      // create-and-solve shortcut resolves the issue the agent solved, not its siblings.
      status: 'new',
      status_id: null,
      assigned_user_id: parentFields.assigned_user_id ?? '',
      queue_id: parentFields.queue_id ?? '',
      tags: [...(parentFields.tags ?? [])],
      device_serial: parentFields.device_serial ?? '',
      custom_fields: { ...(parentFields.custom_fields ?? {}) },
    },
  };
}

/**
 * `child` with field `key` set to `value`, as the card changes it. Changing a form, queue
 * or owner marks it as the agent's choice. Returns a new child; the input is unchanged.
 */
export function setChildField(child, key, value) {
  const touched = childTouched(child);
  return {
    ...child,
    fields: { ...child.fields, [key]: value },
    touched: DEFAULTABLE.includes(key) && !touched.includes(key) ? [...touched, key] : touched,
  };
}

/**
 * A child's problems, keyed the way New.jsx keys the main ticket's — `subject`,
 * `message`, `custom.<key>` — so one card component can point at its own inputs.
 */
export function validateChild(child, formFields, user) {
  const found = {};
  if (!child.composer.subject.trim()) found.subject = 'A subject is required.';
  if (!child.composer.is_internal_note && !user?.can_send_customer_email) {
    found.message = 'Your role cannot send customer-facing email. Switch the message to an internal note.';
  }
  for (const field of missingRequiredFields(formFields, child.fields.custom_fields)) {
    found[`custom.${field.key}`] = `${field.label} is required on this form.`;
  }
  for (const field of invalidNumberFields(formFields, child.fields.custom_fields)) {
    found[`custom.${field.key}`] = `${field.label} must be a number.`;
  }
  return found;
}

/**
 * The POST /api/tickets body for one child, once the parent exists.
 *
 * The caller side — customer, organization, location, CCs, followers, channel — comes
 * from the MAIN ticket, not the child draft, because it is the same call. `parentId` is
 * `tickets.id`, not the public number (§3.3). `assigned_user_id` is always put in the
 * body, '' (Unassigned) as null: absent means "the creator", null means "nobody". Filing
 * a draft then removes the owner, form and queue the agent did not choose (draft-flow.js
 * `fileRequest`, with `childTouched`), so an untouched owner is absent and resolves to
 * whoever files.
 */
export function childBody(child, { caller, fields: parentFields }, parentId, formFields) {
  const body = {
    subject: child.composer.subject.trim(),
    channel: parentFields.channel,
    priority: child.fields.priority,
    status: child.fields.status,
    customer_id: caller.customer.id,
    tags: child.fields.tags,
    parent_ticket_id: parentId,
    assigned_user_id: child.fields.assigned_user_id || null,
  };

  if (child.fields.status_id) body.status_id = child.fields.status_id;
  if (caller.organization) body.organization_id = caller.organization.id;
  if (caller.location) body.location_id = caller.location.id;
  if (child.fields.queue_id) body.queue_id = child.fields.queue_id;
  if (child.fields.form_id) body.form_id = child.fields.form_id;
  if (child.fields.device_serial.trim()) body.device_serial = child.fields.device_serial.trim();
  if (parentFields.follower_user_ids?.length) body.follower_user_ids = parentFields.follower_user_ids;

  const ccs = (parentFields.cc_customer_ids ?? []).filter((id) => id !== caller.customer.id);
  if (ccs.length) body.cc_customer_ids = ccs;

  const custom = encodeCustomFields(formFields, child.fields.custom_fields);
  if (Object.keys(custom).length) body.custom_fields = custom;

  const bodyText = child.composer.body_text.trim();
  if (bodyText) {
    body.initial_message = { body_text: bodyText, is_internal_note: child.composer.is_internal_note };
  }

  return body;
}

/**
 * What the create button should say. One ticket is "Create ticket"; a call that produced
 * three is "Create 3 tickets", because the agent is about to file all of them and the
 * button is the last place that says so.
 */
export function createLabel(childCount, { resolving = false, submitting = false } = {}) {
  if (submitting) return 'Creating…';
  if (childCount > 0) return `Create ${childCount + 1} tickets`;
  return resolving ? 'Create & solve' : 'Create ticket';
}
